import { isDomTargetRef, resolveDomTargetRef } from '@/lib/domTargetRef';

export interface ElementUpdateTargetIds {
  textTargetId?: string;
  srcTargetId?: string;
  hrefTargetId?: string;
}

export interface ElementValueUpdate {
  targetId?: string;
  value: string;
}

export interface ElementContentUpdates {
  text?: string;
  textItems?: ElementValueUpdate[];
  src?: string;
  href?: string;
  hrefItems?: ElementValueUpdate[];
}

export const updateElementInHtml = (
  htmlContent: string,
  elementId: string,
  updates: ElementContentUpdates,
  targetIds?: ElementUpdateTargetIds,
): string => {
  if (!htmlContent || !elementId) return htmlContent;

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const resolvedElementNode = isDomTargetRef(elementId)
    ? resolveDomTargetRef(doc.documentElement, elementId)
    : doc.getElementById(elementId);
  const element =
    resolvedElementNode instanceof Element
      ? resolvedElementNode
      : resolvedElementNode instanceof Text
        ? resolvedElementNode.parentElement
        : null;

  if (!element) {
    console.warn(`[DOMUtils] Element with id "${elementId}" not found.`);
    return htmlContent;
  }

  const getNodeByTargetRef = (targetRef?: string) => {
    if (!targetRef) return null;
    if (isDomTargetRef(targetRef)) {
      return resolveDomTargetRef(element, targetRef);
    }
    return doc.getElementById(targetRef);
  };

  const getElementByTargetId = (targetId?: string) => {
    const target = getNodeByTargetRef(targetId);
    return target instanceof Element ? target : null;
  };

  const resolveTextTarget = () => getNodeByTargetRef(targetIds?.textTargetId) || element;

  const resolveSrcTarget = () => {
    const target = getElementByTargetId(targetIds?.srcTargetId);
    if (target) return target;
    if (['IMG', 'IFRAME', 'VIDEO', 'SOURCE'].includes(element.tagName)) {
      return element;
    }
    return element.querySelector('img, iframe, video, source') || element;
  };

  const resolveHrefTarget = () => {
    const target = getElementByTargetId(targetIds?.hrefTargetId);
    if (target) return target;
    if (element.tagName === 'A') {
      return element;
    }
    return element.querySelector('a[href]') || element;
  };

  const resolveTargetWithFallback = (
    targetId: string | undefined,
    fallbackResolver?: () => Element | null,
  ) => {
    const target = getElementByTargetId(targetId);
    if (target) return target;
    return fallbackResolver?.() || null;
  };

  const resolveTextNodeWithFallback = (
    targetRef: string | undefined,
    fallbackResolver?: () => Node | null,
  ) => getNodeByTargetRef(targetRef) || fallbackResolver?.() || null;

  const setTextTargetContent = (target: Node, nextValue: string) => {
    if (target instanceof HTMLInputElement) {
      target.value = nextValue;
      target.setAttribute('value', nextValue);
      return;
    }

    if (target instanceof HTMLTextAreaElement) {
      target.value = nextValue;
      target.textContent = nextValue;
      return;
    }

    if (target instanceof HTMLSelectElement) {
      const options = Array.from(target.options || []);
      const matchedOption =
        options.find((option) => option.value === nextValue) ||
        options.find((option) => option.text === nextValue);

      if (matchedOption) {
        target.value = matchedOption.value;
        options.forEach((option) => {
          option.selected = option === matchedOption;
        });
      } else {
        target.setAttribute('value', nextValue);
      }
      return;
    }

    if (target instanceof HTMLOptionElement) {
      target.value = nextValue;
      target.textContent = nextValue;
      target.setAttribute('value', nextValue);
      return;
    }

    target.textContent = nextValue;
  };

  const setVisualSourceAttributes = (target: Element, nextSrc: string) => {
    if (['IMG', 'IFRAME', 'VIDEO', 'SOURCE'].includes(target.tagName)) {
      target.setAttribute('src', nextSrc);
    }

    if (target.tagName === 'IMG' || target.tagName === 'SOURCE' || target.hasAttribute('srcset')) {
      target.setAttribute('srcset', nextSrc);
    }

    ['data-src', 'data-srcset', 'data-lazy-src', 'data-lazy-srcset', 'data-original'].forEach(
      (attr) => {
        if (target.hasAttribute(attr)) {
          target.setAttribute(attr, nextSrc);
        }
      },
    );
  };

  if (updates.textItems?.length) {
    updates.textItems.forEach((item) => {
      const target = resolveTextNodeWithFallback(
        item.targetId,
        updates.textItems?.length === 1 ? resolveTextTarget : undefined,
      );
      if (target) {
        setTextTargetContent(target, item.value);
      }
    });
  } else if (updates.text !== undefined) {
    setTextTargetContent(resolveTextTarget(), updates.text);
  }

  if (updates.src !== undefined) {
    const nextSrc = updates.src;
    const srcTarget = resolveSrcTarget();
    setVisualSourceAttributes(srcTarget, nextSrc);

    const picture =
      srcTarget.tagName === 'PICTURE'
        ? srcTarget
        : srcTarget.closest?.('picture') || srcTarget.querySelector?.('picture');

    if (picture) {
      picture.querySelectorAll('source, img').forEach((node) => {
        setVisualSourceAttributes(node, nextSrc);
      });
    }
  }

  if (updates.hrefItems?.length) {
    updates.hrefItems.forEach((item) => {
      const target = resolveTargetWithFallback(
        item.targetId,
        updates.hrefItems?.length === 1 ? resolveHrefTarget : undefined,
      );
      if (target) {
        target.setAttribute('href', item.value);
      }
    });
  } else if (updates.href !== undefined) {
    resolveHrefTarget().setAttribute('href', updates.href);
  }

  return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
};

export const LINKER_CHILD_SCRIPT =
  '<script src="https://cdn.coveauto.ai/d706ee3c-52c4-4998-b90b-2ec73058d22b_linker-child.umd.aiu4qs6n.js" data-linker-init data-debug="true"></script>';
export const SEO_TITLE_MAX_LENGTH = 60;
export const SEO_DESCRIPTION_MAX_LENGTH = 160;
const LINKER_CHILD_SCRIPT_REGEX =
  /<script\b[^>]*\bsrc=["'][^"']*linker-child\.umd\.aiu4qs6n\.js[^"']*["'][^>]*><\/script>\s*/gi;
const SCRIPT_TAG_REGEX = /<script\b[^>]*>[\s\S]*?<\/script>\s*/gi;
const STUDIO_ANALYTICS_CONFIG_SELECTOR = 'script[data-cove-studio-analytics-config]';
const STUDIO_ANALYTICS_RUNTIME_SELECTOR = 'script[data-cove-studio-analytics-runtime]';
const GENERATED_HTML_VIEWPORT_META_CONTENT =
  'width=device-width, initial-scale=1, viewport-fit=cover';
const GENERATED_HTML_SAFARI_FIX_STYLE_ATTR = 'data-cove-safari-compat-style';
const GENERATED_HTML_SAFARI_FIX_SCRIPT_ATTR = 'data-cove-safari-compat-script';
const GENERATED_HTML_SAFARI_FIX_CSS = `
html {
  min-height: 100%;
  min-height: -webkit-fill-available;
  -webkit-text-size-adjust: 100%;
}

body {
  min-height: 100vh;
  min-height: 100svh;
  min-height: 100dvh;
  min-height: var(--cove-safe-vh, 100vh);
}

html.cove-safari-mobile,
html.cove-safari-mobile body {
  min-height: var(--cove-safe-vh, 100vh);
}

html.cove-safari-mobile .reveal,
html.cove-safari-mobile .reveal.in {
  opacity: 1 !important;
  visibility: visible !important;
  transform: none !important;
}

html.cove-safari-mobile a,
html.cove-safari-mobile button,
html.cove-safari-mobile [role='button'],
html.cove-safari-mobile input,
html.cove-safari-mobile select,
html.cove-safari-mobile textarea,
html.cove-safari-mobile label {
  touch-action: manipulation;
}
`;
const GENERATED_HTML_SAFARI_FIX_SCRIPT = `(function () {
  try {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    var ua = navigator.userAgent || '';
    var isIOS =
      /iP(ad|hone|od)/.test(ua) ||
      (navigator.platform === 'MacIntel' && Number(navigator.maxTouchPoints || 0) > 1);
    var isSafari =
      /Safari/i.test(ua) &&
      !/Chrome|CriOS|Edg|EdgiOS|FxiOS|OPiOS|OPR|SamsungBrowser|Android/i.test(ua);
    if (!isIOS && !isSafari) return;

    var root = document.documentElement;
    root.classList.add('cove-safari-mobile');

    var setSafeViewportHeight = function () {
      var nextHeight =
        (window.visualViewport && window.visualViewport.height) || window.innerHeight || 0;
      if (nextHeight > 0) {
        root.style.setProperty('--cove-safe-vh', nextHeight + 'px');
      }
    };

    var revealHiddenContent = function () {
      var revealNodes = document.querySelectorAll('.reveal');
      for (var i = 0; i < revealNodes.length; i += 1) {
        var revealNode = revealNodes[i];
        revealNode.classList.add('in');
        if (revealNode.style) {
          revealNode.style.opacity = '1';
          revealNode.style.visibility = 'visible';
          if (revealNode.style.transform) {
            revealNode.style.transform = 'none';
          }
        }
      }
    };

    var disableInvisibleFixedBlockers = function () {
      if (!document.body) return;
      var nodes = document.body.querySelectorAll('*');
      for (var i = 0; i < nodes.length; i += 1) {
        var node = nodes[i];
        if (!(node instanceof HTMLElement)) continue;
        var computed = window.getComputedStyle(node);
        if (computed.position !== 'fixed' || computed.pointerEvents === 'none') continue;
        var opacity = Number(computed.opacity || '1');
        if (computed.visibility === 'hidden' || opacity < 0.05) {
          node.style.pointerEvents = 'none';
          node.setAttribute('data-cove-safari-unblocked', 'true');
        }
      }
    };

    var refresh = function () {
      setSafeViewportHeight();
      revealHiddenContent();
      disableInvisibleFixedBlockers();
    };

    refresh();
    window.setTimeout(refresh, 60);
    window.setTimeout(refresh, 260);
    window.addEventListener('resize', refresh, { passive: true });
    window.addEventListener('orientationchange', refresh, { passive: true });
    if (window.visualViewport && window.visualViewport.addEventListener) {
      window.visualViewport.addEventListener('resize', refresh, { passive: true });
    }

    if (document.readyState === 'complete') {
      refresh();
    } else {
      window.addEventListener('load', refresh, { once: true });
    }
  } catch (error) {
    console.warn('[GeneratedHtmlSafariCompat]', error);
  }
})();`;
const LEGACY_GOOGLE_ADS_SCRIPT_MARKERS = [
  /google-ads-tracking/i,
  /\bgadsConfig\b/,
  /trackFormSubmission/,
  /trackCTAButtonClick/,
  /trackTimeOnPage/,
  /trackScrollDepth/,
  /data-gads/,
  /data-role=["']google-ads-conversion-runtime["']/i,
  /googleadservices\.com\/pagead\/conversion_async\.js/i,
  /googleads\.g\.doubleclick\.net\/pagead\/viewthroughconversion\//i,
];
const LEGACY_LANDING_PAGE_TRACKING_SCRIPT_MARKERS = [
  /landing_page_[a-z0-9_]+/i,
  /custom_tracking_ready/i,
  /custom_cta_click/i,
  /data-role=["']growthtrack-runtime["']/i,
  /event_category\s*:\s*["']site_conversion["']/i,
  /event_label\s*:\s*["']time_on_page["']/i,
  /event_label\s*:\s*["']scroll_depth["']/i,
];

const isLegacyGoogleAdsScript = (scriptContent: string) =>
  LEGACY_GOOGLE_ADS_SCRIPT_MARKERS.some((marker) => marker.test(scriptContent));

const isLegacyLandingPageTrackingScript = (scriptContent: string) =>
  LEGACY_LANDING_PAGE_TRACKING_SCRIPT_MARKERS.some((marker) => marker.test(scriptContent));

const trimMetadataValue = (value?: string | null) => String(value || '').trim();

const clampMetadataValue = (value: string | null | undefined, maxLength: number) =>
  trimMetadataValue(value).slice(0, maxLength);

export const injectLinkerScript = (html: string): string => {
  if (!html) return html;

  LINKER_CHILD_SCRIPT_REGEX.lastIndex = 0;
  if (LINKER_CHILD_SCRIPT_REGEX.test(html)) {
    LINKER_CHILD_SCRIPT_REGEX.lastIndex = 0;
    return html;
  }

  const bodyEndRegex = /<\/body>/i;
  if (bodyEndRegex.test(html)) {
    return html.replace(bodyEndRegex, `${LINKER_CHILD_SCRIPT}</body>`);
  }
  return `${html}${LINKER_CHILD_SCRIPT}`;
};

export const stripLinkerScript = (html: string): string => {
  if (!html) return html;
  return html.replace(LINKER_CHILD_SCRIPT_REGEX, '').trim();
};

const GENERATED_FORM_FIELD_ALIASES: Record<string, string> = {
  pain_point: 'bottleneck_details',
  painPoint: 'bottleneck_details',
  'pain-point': 'bottleneck_details',
};

const replaceGeneratedFormFieldKey = (value: string): string => {
  let nextValue = value;

  Object.entries(GENERATED_FORM_FIELD_ALIASES).forEach(([from, to]) => {
    const escapedFrom = from.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    nextValue = nextValue
      .replace(new RegExp(`(["'\`])${escapedFrom}\\1`, 'g'), (_match, quote: string) => {
        return `${quote}${to}${quote}`;
      })
      .replace(new RegExp(`\\b${escapedFrom}\\b(?=\\s*:)`, 'g'), to);
  });

  return nextValue;
};

/**
 * Keep generated form field names aligned with the backend entity schema.
 * These aliases are limited to known generated form field keys.
 */
export const normalizeGeneratedFormFieldNames = (html: string): string => {
  if (!html) return html;

  const normalizedHtml = replaceGeneratedFormFieldKey(html);
  const parser = new DOMParser();
  const doc = parser.parseFromString(normalizeGeneratedHtml(normalizedHtml), 'text/html');
  let changed = false;

  doc.querySelectorAll('*').forEach((node) => {
    Array.from(node.attributes).forEach((attribute) => {
      const nextValue = replaceGeneratedFormFieldKey(attribute.value);
      if (nextValue !== attribute.value) {
        node.setAttribute(attribute.name, nextValue);
        changed = true;
      }
    });
  });

  doc.querySelectorAll('script').forEach((script) => {
    const scriptContent = script.textContent || '';
    const nextContent = replaceGeneratedFormFieldKey(scriptContent);
    if (nextContent !== scriptContent) {
      script.textContent = nextContent;
      changed = true;
    }
  });

  if (changed) return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
  return normalizedHtml;
};

export const stripLegacyGoogleAdsTrackingScript = (html: string): string => {
  if (!html) return html;

  return html.replace(SCRIPT_TAG_REGEX, (scriptTag) =>
    isLegacyGoogleAdsScript(scriptTag) ? '' : scriptTag,
  );
};

export const stripLegacyLandingPageTrackingScript = (html: string): string => {
  if (!html) return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  let removedAnalyticsConfig = false;
  let removedAnyScript = false;

  Array.from(doc.querySelectorAll('script')).forEach((script) => {
    const scriptContent = `${script.outerHTML}\n${script.textContent || ''}`;
    if (!isLegacyLandingPageTrackingScript(scriptContent)) return;

    if (script.matches(STUDIO_ANALYTICS_CONFIG_SELECTOR)) {
      removedAnalyticsConfig = true;
    }

    removedAnyScript = true;
    script.remove();
  });

  if (removedAnalyticsConfig) {
    doc.querySelectorAll(STUDIO_ANALYTICS_RUNTIME_SELECTOR).forEach((node) => node.remove());
  }

  if (!removedAnyScript) return html;
  return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
};

export const removeLegacyGoogleAdsTrackingScriptsFromDocument = (
  doc: Document | null | undefined,
) => {
  if (!doc) return;

  Array.from(doc.querySelectorAll('script')).forEach((script) => {
    const scriptContent = `${script.outerHTML}\n${script.textContent || ''}`;
    if (isLegacyGoogleAdsScript(scriptContent)) {
      script.remove();
    }
  });
};

export const removeLegacyLandingPageTrackingScriptsFromDocument = (
  doc: Document | null | undefined,
) => {
  if (!doc) return;

  let removedAnalyticsConfig = false;

  Array.from(doc.querySelectorAll('script')).forEach((script) => {
    const scriptContent = `${script.outerHTML}\n${script.textContent || ''}`;
    if (!isLegacyLandingPageTrackingScript(scriptContent)) return;

    if (script.matches(STUDIO_ANALYTICS_CONFIG_SELECTOR)) {
      removedAnalyticsConfig = true;
    }

    script.remove();
  });

  if (removedAnalyticsConfig) {
    doc.querySelectorAll(STUDIO_ANALYTICS_RUNTIME_SELECTOR).forEach((node) => node.remove());
  }
};

export const normalizeGeneratedHtml = (html: string): string => {
  const raw = String(html || '');
  if (!raw) return '';

  const trimmed = raw.trim();
  const lower = trimmed.toLowerCase();
  const doctypeIndex = lower.indexOf('<!doctype');
  const htmlIndex = lower.indexOf('<html');
  const bodyIndex = lower.indexOf('<body');
  const headIndex = lower.indexOf('<head');

  const startIndexCandidates = [doctypeIndex, htmlIndex, bodyIndex, headIndex].filter(
    (index) => index >= 0,
  );

  if (startIndexCandidates.length === 0) {
    return trimmed;
  }

  const startIndex = Math.min(...startIndexCandidates);
  return trimmed.slice(startIndex).trim();
};

const REVEAL_CLASS_TOKEN_REGEX = /\breveal\b/i;
const INLINE_HIDDEN_REVEAL_STYLE_REGEXES = [
  /(^|;)\s*opacity\s*:\s*0(?:\.0+)?\s*(?=;|$)/gi,
  /(^|;)\s*visibility\s*:\s*hidden\s*(?=;|$)/gi,
];
const PREVIEW_REVEAL_FIX_STYLE_ATTR = 'data-studio-preview-reveal-fix';
const PREVIEW_REVEAL_FIX_CSS = `
.reveal,
.reveal.in {
  opacity: 1 !important;
  visibility: visible !important;
}
`;

const stripInlineHiddenRevealStyles = (element: Element) => {
  const style = element.getAttribute('style');
  if (!style) return;

  const nextStyle = INLINE_HIDDEN_REVEAL_STYLE_REGEXES.reduce(
    (value, pattern) => value.replace(pattern, '$1'),
    style,
  )
    .replace(/\s*;\s*;\s*/g, '; ')
    .replace(/^;\s*|\s*;$/g, '')
    .trim();

  if (nextStyle) {
    element.setAttribute('style', nextStyle);
    return;
  }

  element.removeAttribute('style');
};

const addRevealVisibleState = (doc: Document) => {
  const revealElements = Array.from(doc.querySelectorAll('.reveal'));
  if (!revealElements.length) return false;

  revealElements.forEach((element) => {
    element.classList.add('in');
    stripInlineHiddenRevealStyles(element);
  });

  return true;
};

const ensureDocumentHead = (doc: Document) => {
  const htmlElement = doc.documentElement;
  if (!htmlElement) return null;

  let head = doc.head;
  if (!head) {
    head = doc.createElement('head');
    if (doc.body) {
      htmlElement.insertBefore(head, doc.body);
    } else {
      htmlElement.appendChild(head);
    }
  }

  return head;
};

const ensureViewportMeta = (doc: Document) => {
  const head = ensureDocumentHead(doc);
  if (!head) return false;

  let viewportMeta = head.querySelector('meta[name="viewport"]');
  const nextContent = GENERATED_HTML_VIEWPORT_META_CONTENT;
  if (!viewportMeta) {
    viewportMeta = doc.createElement('meta');
    viewportMeta.setAttribute('name', 'viewport');
    viewportMeta.setAttribute('content', nextContent);
    head.insertBefore(viewportMeta, head.firstChild);
    return true;
  }

  if (viewportMeta.getAttribute('content') === nextContent) {
    return false;
  }

  viewportMeta.setAttribute('content', nextContent);
  return true;
};

const ensurePreviewRevealFixStyle = (doc: Document) => {
  const head = ensureDocumentHead(doc);
  if (!head) return false;

  const existingStyle = head.querySelector(`style[${PREVIEW_REVEAL_FIX_STYLE_ATTR}]`);
  if (existingStyle) {
    if (existingStyle.textContent === PREVIEW_REVEAL_FIX_CSS) return false;
    existingStyle.textContent = PREVIEW_REVEAL_FIX_CSS;
    return true;
  }

  const style = doc.createElement('style');
  style.setAttribute(PREVIEW_REVEAL_FIX_STYLE_ATTR, 'true');
  style.textContent = PREVIEW_REVEAL_FIX_CSS;
  head.appendChild(style);
  return true;
};

const ensureGeneratedSafariFixStyle = (doc: Document) => {
  const head = ensureDocumentHead(doc);
  if (!head) return false;

  const existingStyle = head.querySelector(`style[${GENERATED_HTML_SAFARI_FIX_STYLE_ATTR}]`);
  if (existingStyle) {
    if (existingStyle.textContent === GENERATED_HTML_SAFARI_FIX_CSS) return false;
    existingStyle.textContent = GENERATED_HTML_SAFARI_FIX_CSS;
    return true;
  }

  const style = doc.createElement('style');
  style.setAttribute(GENERATED_HTML_SAFARI_FIX_STYLE_ATTR, 'true');
  style.textContent = GENERATED_HTML_SAFARI_FIX_CSS;
  head.appendChild(style);
  return true;
};

const ensureGeneratedSafariFixScript = (doc: Document) => {
  const htmlElement = doc.documentElement;
  if (!htmlElement) return false;

  const existingScript = doc.querySelector(`script[${GENERATED_HTML_SAFARI_FIX_SCRIPT_ATTR}]`);
  if (existingScript) {
    if (existingScript.textContent === GENERATED_HTML_SAFARI_FIX_SCRIPT) return false;
    existingScript.textContent = GENERATED_HTML_SAFARI_FIX_SCRIPT;
    return true;
  }

  const script = doc.createElement('script');
  script.setAttribute(GENERATED_HTML_SAFARI_FIX_SCRIPT_ATTR, 'true');
  script.textContent = GENERATED_HTML_SAFARI_FIX_SCRIPT;
  if (doc.body) {
    doc.body.appendChild(script);
  } else {
    htmlElement.appendChild(script);
  }
  return true;
};

const ensureGeneratedHtmlSafariCompat = (doc: Document) => {
  let changed = false;
  changed = ensureViewportMeta(doc) || changed;
  changed = ensureGeneratedSafariFixStyle(doc) || changed;
  changed = ensureGeneratedSafariFixScript(doc) || changed;
  return changed;
};

export const ensureStaticRevealContentVisible = (html: string): string => {
  if (!html) return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  let changed = ensureGeneratedHtmlSafariCompat(doc);
  if (REVEAL_CLASS_TOKEN_REGEX.test(html)) {
    changed = addRevealVisibleState(doc) || changed;
    changed = ensurePreviewRevealFixStyle(doc) || changed;
  }
  if (!changed) return html;

  return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
};

export const stabilizePreviewRevealContent = (html: string): string => {
  if (!html) return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  let changed = ensureGeneratedHtmlSafariCompat(doc);
  if (REVEAL_CLASS_TOKEN_REGEX.test(html)) {
    changed = addRevealVisibleState(doc) || changed;
    changed = ensurePreviewRevealFixStyle(doc) || changed;
  }
  if (!changed) return html;

  return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
};

export const applyBaseHrefToHtml = (html: string, baseHref: string): string => {
  if (!html || !baseHref) return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(normalizeGeneratedHtml(html), 'text/html');
  const htmlElement = doc.documentElement;
  if (!htmlElement) return html;

  const head = ensureDocumentHead(doc);
  if (!head) return html;

  let base = head.querySelector('base');
  if (!base) {
    base = doc.createElement('base');
    head.insertBefore(base, head.firstChild);
  }
  base.setAttribute('href', baseHref);

  return '<!DOCTYPE html>\n' + htmlElement.outerHTML;
};

export const buildDraftPreviewHtml = (html: string): string =>
  injectLinkerScript(
    stabilizePreviewRevealContent(
      ensureStaticRevealContentVisible(
        normalizeGeneratedFormFieldNames(normalizeGeneratedHtml(html)),
      ),
    ),
  );

export const buildPermanentPreviewHtml = (html: string, baseHref: string): string =>
  buildDraftPreviewHtml(applyBaseHrefToHtml(html, baseHref));

export const extractMetadataFromHtml = (htmlContent: string) => {
  if (!htmlContent) {
    return {
      title: '',
      description: '',
      keywords: '',
      ogTitle: '',
      ogDescription: '',
      ogImageUrl: '',
      allowIndexing: true,
      faviconUrl: '',
    };
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const head = doc.head;

  const readMeta = (name: string, property: boolean = false) => {
    if (!head) return '';
    const attr = property ? 'property' : 'name';
    return trimMetadataValue(
      head.querySelector(`meta[${attr}="${name}"]`)?.getAttribute('content'),
    );
  };

  const robots = readMeta('robots');
  const faviconHref = trimMetadataValue(
    head?.querySelector('link[rel*="icon"]')?.getAttribute('href'),
  );

  return {
    title: clampMetadataValue(doc.title, SEO_TITLE_MAX_LENGTH),
    description: clampMetadataValue(readMeta('description'), SEO_DESCRIPTION_MAX_LENGTH),
    keywords: readMeta('keywords'),
    ogTitle: clampMetadataValue(readMeta('og:title', true), SEO_TITLE_MAX_LENGTH),
    ogDescription: clampMetadataValue(readMeta('og:description', true), SEO_DESCRIPTION_MAX_LENGTH),
    ogImageUrl: readMeta('og:image', true),
    allowIndexing: !/noindex/i.test(robots),
    faviconUrl: faviconHref,
  };
};

export const normalizeSeoMetadataInput = (metadata: {
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImageUrl?: string;
  allowIndexing?: boolean;
  faviconUrl?: string;
}) => ({
  title: clampMetadataValue(metadata.title, SEO_TITLE_MAX_LENGTH),
  description: clampMetadataValue(metadata.description, SEO_DESCRIPTION_MAX_LENGTH),
  keywords: trimMetadataValue(metadata.keywords),
  ogTitle: clampMetadataValue(metadata.ogTitle, SEO_TITLE_MAX_LENGTH),
  ogDescription: clampMetadataValue(metadata.ogDescription, SEO_DESCRIPTION_MAX_LENGTH),
  ogImageUrl: trimMetadataValue(metadata.ogImageUrl),
  allowIndexing: metadata.allowIndexing ?? true,
  faviconUrl: trimMetadataValue(metadata.faviconUrl),
});

export const updateMetadataInHtml = (
  htmlContent: string,
  metadata: {
    title?: string;
    description?: string;
    keywords?: string;
    ogTitle?: string;
    ogDescription?: string;
    ogImageUrl?: string;
    allowIndexing?: boolean;
    faviconUrl?: string;
  },
): string => {
  if (!htmlContent) return htmlContent;

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const head = doc.head;

  if (!head) return htmlContent;

  const setMeta = (name: string, content: string, property: boolean = false) => {
    const attr = property ? 'property' : 'name';
    let meta = head.querySelector(`meta[${attr}="${name}"]`);
    const normalizedContent = trimMetadataValue(content);

    if (!normalizedContent) {
      meta?.remove();
      return;
    }

    if (!meta) {
      meta = doc.createElement('meta');
      meta.setAttribute(attr, name);
      head.appendChild(meta);
    }
    meta.setAttribute('content', normalizedContent);
  };

  if (metadata.title !== undefined) {
    let titleElem = head.querySelector('title');
    if (!titleElem) {
      titleElem = doc.createElement('title');
      head.appendChild(titleElem);
    }
    titleElem.textContent = metadata.title;
  }

  if (metadata.description !== undefined) setMeta('description', metadata.description);
  if (metadata.keywords !== undefined) setMeta('keywords', metadata.keywords);

  if (metadata.ogTitle !== undefined) setMeta('og:title', metadata.ogTitle, true);
  if (metadata.ogDescription !== undefined) setMeta('og:description', metadata.ogDescription, true);
  if (metadata.ogImageUrl !== undefined) setMeta('og:image', metadata.ogImageUrl, true);

  if (metadata.allowIndexing !== undefined) {
    setMeta('robots', metadata.allowIndexing ? 'index, follow' : 'noindex, nofollow');
  }

  if (metadata.faviconUrl !== undefined) {
    let link = head.querySelector('link[rel*="icon"]');
    const faviconUrl = trimMetadataValue(metadata.faviconUrl);

    if (!faviconUrl) {
      link?.remove();
      return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
    }

    if (!link) {
      link = doc.createElement('link');
      link.setAttribute('rel', 'icon');
      head.appendChild(link);
    }
    link.setAttribute('href', faviconUrl);
  }

  return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
};
